export interface Lesson {
  id: string;
  title: string;
  duration: string;
  type: 'video' | 'quiz' | 'project' | 'reading';
  preview?: boolean;
}

export interface Chapter {
  id: string;
  title: string;
  description: string;
  lessons: Lesson[];
}

export interface CourseReview {
  id: string;
  name: string;
  avatar: string;
  rating: number;
  date: string;
  content: string;
  company: string;
  result: string;
}

export interface CourseInstructorDetail {
  name: string;
  title: string;
  company: string;
  companyLogo: string;
  companyIcon?: string;
  avatar: string;
  bio: string;
  experience: string;
  stats: { label: string; value: string }[];
  expertise: string[];
  mentorId?: string;
}

export interface Course {
  id: string;
  categoryId?: string;
  isPurchased?: boolean;
  title: string;
  subtitle: string;
  description?: string;
  instructor: string;
  instructorCompany: string;
  instructorDetail?: CourseInstructorDetail;
  level: string;
  duration: string;
  students: number;
  rating: number;
  reviews: number;
  price: number;
  originalPrice: number;
  memberPrice?: number;
  tag: string;
  tagColor: string;
  gradient: string;
  icon: string;
  category: string;
  topics: string[];
  learningObjectives?: string[];
  requirements?: string[];
  discountEndTime?: string;
  courseIncludes?: string[];
  courseInfo?: Array<{ label: string; value: string }>;
  chapters?: Chapter[];
  courseReviews?: CourseReview[];
  lastUpdated?: string;
  language?: string;
  certificate?: boolean;
}

export const allCourses: Course[] = [
  {
    id: 'ai-pm-bootcamp',
    title: 'AI 產品經理全攻略',
    subtitle: '從零到外商 PM Offer 的完整路徑',
    description: '本課程由 Google 資深產品經理 James Chen 傾力打造，系統講解 AI 時代產品經理所需的核心技能。從產品思維框架到 AI 工具整合，從使用者研究到資料驅動決策，涵蓋完整的 PM 職涯路徑。課程包含 30+ 真實案例分析和 8 個動手實戰專案。',
    instructor: 'James Chen',
    instructorCompany: 'Google',
    instructorDetail: {
      name: 'James Chen',
      title: 'Senior Product Manager',
      company: 'Google',
      companyLogo: '/assets/readdy/logo-google-1-80x80-squarish-1c18494f.jpg',
      avatar: '/assets/readdy/instructor-james-1-200x200-squarish-2447719d.jpg',
      bio: 'James Chen 在 Google 擔任高級產品經理超過 8 年，主導了多個月活過億的核心產品迭代。曾在 Google Search、Google Maps 和 Google Assistant 團隊工作，深度參與 AI 功能的產品化落地。擁有史丹佛大學 CS + MBA 雙學位，是灣區最活躍的 PM 社群導師之一，累計協助超過 500 名學員成功轉型到頂尖科技公司。',
      experience: '8 年以上大廠 PM 經驗',
      stats: [
        { label: '輔導學員', value: '500+' },
        { label: '課程評分', value: '4.94' },
        { label: '講授課程', value: '3 門' },
        { label: '工作經驗', value: '8 年+' },
      ],
      expertise: ['AI 產品設計', '使用者成長', 'OKR 方法論', '資料決策', 'PM 面試', 'Go-to-Market'],
      mentorId: 'james-chen',
    },
    level: '進階',
    duration: '24 小時',
    students: 3842,
    rating: 4.94,
    reviews: 621,
    price: 499,
    originalPrice: 899,
    tag: '暢銷',
    tagColor: 'bg-amber-500',
    gradient: 'from-violet-600 to-indigo-700',
    icon: 'ri-product-hunt-line',
    category: '產品管理',
    topics: ['AI 產品設計', 'PM 面試技巧', 'OKR 方法論', '使用者成長'],
    lastUpdated: '2026 年 2 月',
    language: '中文',
    certificate: true,
    learningObjectives: [
      '系統掌握 AI 時代產品經理的核心思維框架和方法論',
      '學會運用 ChatGPT、Midjourney 等 AI 工具提升 10 倍工作效率',
      '掌握大廠 PM 面試的完整流程和高分回答策略',
      '能夠獨立設計並落地一個 AI 驅動的產品功能',
      '建立完整的資料分析和 A/B 測試能力',
      '了解如何在 FAANG 級公司推進產品 Roadmap 和跨團隊協作',
    ],
    requirements: [
      '有 1-3 年網路或科技相關工作經驗',
      '對產品經理職位有基本了解',
      '具備基礎的英文閱讀能力',
      '不需要程式設計背景',
    ],
    chapters: [
      {
        id: 'ch1',
        title: '第一章：AI 時代的 PM 思維框架',
        description: '重新理解產品經理在 AI 浪潮中的角色與價值',
        lessons: [
          { id: 'l1-1', title: '歡迎 & 課程概述', duration: '8 分鐘', type: 'video', preview: true },
          { id: 'l1-2', title: 'AI 浪潮下 PM 角色的演變', duration: '22 分鐘', type: 'video', preview: true },
          { id: 'l1-3', title: '頂尖 PM 的思維模型：從功能到體驗再到生態', duration: '35 分鐘', type: 'video' },
          { id: 'l1-4', title: 'Google 內部 PRD 範本解析', duration: '28 分鐘', type: 'video' },
          { id: 'l1-5', title: '第一章知識測驗', duration: '15 分鐘', type: 'quiz' },
        ],
      },
      {
        id: 'ch2',
        title: '第二章：使用者研究與需求挖掘',
        description: '用 AI 工具加速使用者洞察，讓資料說話',
        lessons: [
          { id: 'l2-1', title: '質化 vs 量化研究：何時用哪種方法', duration: '30 分鐘', type: 'video' },
          { id: 'l2-2', title: '用 AI 輔助使用者訪談分析', duration: '42 分鐘', type: 'video' },
          { id: 'l2-3', title: '建立使用者畫像與 Jobs-to-be-Done', duration: '38 分鐘', type: 'video' },
          { id: 'l2-4', title: '競品分析實戰演練', duration: '50 分鐘', type: 'project' },
          { id: 'l2-5', title: '使用者調研報告撰寫指南', duration: '20 分鐘', type: 'reading' },
        ],
      },
      {
        id: 'ch3',
        title: '第三章：產品設計與原型迭代',
        description: '從線框圖到高擬真原型，快速驗證產品假設',
        lessons: [
          { id: 'l3-1', title: 'Figma 快速入門（PM 視角）', duration: '45 分鐘', type: 'video' },
          { id: 'l3-2', title: 'AI 功能的 UX 設計原則', duration: '40 分鐘', type: 'video' },
          { id: 'l3-3', title: '從 0 到 1：AI 搜尋功能原型設計', duration: '60 分鐘', type: 'project' },
          { id: 'l3-4', title: '使用者測試與快速迭代方法', duration: '35 分鐘', type: 'video' },
        ],
      },
      {
        id: 'ch4',
        title: '第四章：資料驅動決策',
        description: '讓每一個產品決策都有資料支撐',
        lessons: [
          { id: 'l4-1', title: 'PM 必備資料分析框架', duration: '38 分鐘', type: 'video' },
          { id: 'l4-2', title: 'A/B 測試設計與結果解讀', duration: '55 分鐘', type: 'video' },
          { id: 'l4-3', title: 'North Star Metric 的選擇邏輯', duration: '30 分鐘', type: 'video' },
          { id: 'l4-4', title: '資料分析專案實戰', duration: '70 分鐘', type: 'project' },
          { id: 'l4-5', title: '第四章知識測驗', duration: '20 分鐘', type: 'quiz' },
        ],
      },
      {
        id: 'ch5',
        title: '第五章：FAANG PM 面試全攻略',
        description: '拆解每一種題型，手把手帶你練到滿分',
        lessons: [
          { id: 'l5-1', title: '產品設計題：通用解題框架', duration: '48 分鐘', type: 'video' },
          { id: 'l5-2', title: '估算題：結構化思維拆解', duration: '42 分鐘', type: 'video' },
          { id: 'l5-3', title: '行為題：STAR 法則進階版', duration: '35 分鐘', type: 'video' },
          { id: 'l5-4', title: '真實 Google 面試題模擬演練', duration: '90 分鐘', type: 'project' },
          { id: 'l5-5', title: '薪資談判策略與 Offer 比較', duration: '40 分鐘', type: 'video' },
        ],
      },
    ],
    courseReviews: [
      {
        id: 'r1',
        name: '林曉宇',
        avatar: '/assets/readdy/review-1-80x80-squarish-9414a6c6.jpg',
        rating: 5,
        date: '2026 年 1 月',
        content: '從轉行新手到拿到 Google Taiwan PM Offer，這門課是最關鍵的拼圖。James 的面試框架讓我在 20+ 場面試中每次都能結構清晰地表達，強烈推薦！',
        company: 'Google Taiwan',
        result: '成功拿到 Google PM Offer',
      },
      {
        id: 'r2',
        name: '陳明哲',
        avatar: '/assets/readdy/review-2-80x80-squarish-caaaeb8d.jpg',
        rating: 5,
        date: '2025 年 12 月',
        content: '課程內容非常實戰，不是那種純理論的。特別是第五章的面試題演練，每道題 James 都給出了不同層次的回答版本，很清楚地知道差距在哪裡。',
        company: 'Meta',
        result: '跳槽薪資提升 85%',
      },
      {
        id: 'r3',
        name: 'Vivian Huang',
        avatar: '/assets/readdy/review-3-80x80-squarish-f8d27ce3.jpg',
        rating: 5,
        date: '2025 年 11 月',
        content: '在台灣科技業工作 5 年後想轉到外商，這門課給了我很清晰的路線圖。資料驅動那章對我幫助最大，面試時被問到資料相關問題完全不慌了。',
        company: 'Shopee',
        result: '成功轉型外商 PM',
      },
    ],
  },
  {
    id: 'faang-interview-sprint',
    title: '頂尖外商面試衝刺營',
    subtitle: 'FAANG 系統設計 + 行為面試雙線突破',
    description: '由頂尖外商首席研究科學家主講，這是市面上最硬核的外商面試備戰課程。涵蓋全球頭部科技公司的完整面試流程，從履歷篩選到 Final Round Offer 談判全程陪跑。課程包含 50+ 真實大廠面試題和詳細解析。',
    instructor: 'NexusGo 導師',
    instructorCompany: 'NVIDIA',
    instructorDetail: {
      name: 'NexusGo 導師',
      title: 'Principal Research Scientist',
      company: 'NVIDIA',
      companyLogo: '/assets/readdy/logo-nvidia-1-80x80-squarish-ddceca79.jpg',
      avatar: '/assets/readdy/instructor-elena-1-200x200-squarish-93e83ebd.jpg',
      bio: 'Elena Volkov 擁有 MIT 資訊科學博士學位，專注於大規模機器學習系統研究。在 NVIDIA 擔任首席研究科學家，主導多個 GPU 加速 AI 訓練框架的研發，發表頂級會議論文 20+ 篇。作為面試官親歷 300+ 場 FAANG 級別面試，深知評審視角，能精準指出候選人的提升空間。',
      experience: '12 年 AI 研究與工程經驗',
      stats: [
        { label: '輔導學員', value: '300+' },
        { label: '課程評分', value: '4.97' },
        { label: '學術論文', value: '20+ 篇' },
        { label: '工作經驗', value: '12 年+' },
      ],
      expertise: ['系統設計', 'ML 架構', '演算法優化', '行為面試', 'FAANG 面試流程', '薪資談判'],
      mentorId: 'elena-volkov',
    },
    level: '高階',
    duration: '32 小時',
    students: 2956,
    rating: 4.97,
    reviews: 487,
    price: 699,
    originalPrice: 1299,
    tag: '最熱',
    tagColor: 'bg-rose-500',
    gradient: 'from-emerald-600 to-teal-700',
    icon: 'ri-trophy-line',
    category: 'AI 研究',
    topics: ['系統設計', '行為面試', 'LeetCode 策略', '薪資談判'],
    lastUpdated: '2026 年 3 月',
    language: '中文',
    certificate: true,
    learningObjectives: [
      '掌握 FAANG 系統設計面試的完整解題框架',
      '深度理解行為面試評分標準，學會打動面試官的 STAR 敘事',
      '掌握高效的 LeetCode 刷題策略，2 個月內達到面試水準',
      '了解 5 大頂尖公司的差異化面試風格與準備策略',
      '掌握 Final Round 到 Offer 的談判技巧，平均多談 20%',
      '建立完整的面試心態管理體系',
    ],
    requirements: [
      '有程式設計基礎（任何語言均可）',
      '至少 2 年相關工作經驗',
      '目標是進入 FAANG 或同等級別公司',
      '能投入每天 2-3 小時備戰時間',
    ],
    chapters: [
      {
        id: 'ch1',
        title: '第一章：FAANG 面試流程全解析',
        description: '知己知彼，了解各公司差異化的面試體系',
        lessons: [
          { id: 'l1-1', title: '開營說明 & 學習路徑規劃', duration: '15 分鐘', type: 'video', preview: true },
          { id: 'l1-2', title: 'Google 面試流程 Deep Dive', duration: '40 分鐘', type: 'video', preview: true },
          { id: 'l1-3', title: 'Meta / Amazon / NVIDIA 面試比較', duration: '45 分鐘', type: 'video' },
          { id: 'l1-4', title: '履歷優化：從 ATS 到 Hiring Manager', duration: '35 分鐘', type: 'video' },
          { id: 'l1-5', title: 'LinkedIn 冷連結策略', duration: '25 分鐘', type: 'reading' },
        ],
      },
      {
        id: 'ch2',
        title: '第二章：系統設計面試精講',
        description: '用工程師思維設計可擴展的大型系統',
        lessons: [
          { id: 'l2-1', title: '系統設計解題框架 FRDAS', duration: '50 分鐘', type: 'video' },
          { id: 'l2-2', title: '案例：設計 Twitter Feed 系統', duration: '65 分鐘', type: 'video' },
          { id: 'l2-3', title: '案例：設計分散式快取系統', duration: '70 分鐘', type: 'video' },
          { id: 'l2-4', title: '案例：設計 ML 推薦系統', duration: '80 分鐘', type: 'video' },
          { id: 'l2-5', title: '高頻系統設計題合集（10 題）', duration: '120 分鐘', type: 'project' },
        ],
      },
      {
        id: 'ch3',
        title: '第三章：LeetCode 高效刷題策略',
        description: '不是刷題數量，而是刷題品質和模式識別',
        lessons: [
          { id: 'l3-1', title: '高頻題型分析：FAANG 偏好模式', duration: '42 分鐘', type: 'video' },
          { id: 'l3-2', title: '滑動視窗 & 雙指標專題', duration: '55 分鐘', type: 'video' },
          { id: 'l3-3', title: 'DP 動態規劃 10 種核心範本', duration: '90 分鐘', type: 'video' },
          { id: 'l3-4', title: '圖論 & BFS/DFS 實戰', duration: '65 分鐘', type: 'video' },
          { id: 'l3-5', title: '計時模擬面試（10 題精選）', duration: '100 分鐘', type: 'project' },
        ],
      },
      {
        id: 'ch4',
        title: '第四章：行為面試的藝術',
        description: '用故事打動面試官，展現真正的 Leadership',
        lessons: [
          { id: 'l4-1', title: 'Amazon Leadership Principles 深度解析', duration: '48 分鐘', type: 'video' },
          { id: 'l4-2', title: '建立你的高品質 STAR 故事庫', duration: '55 分鐘', type: 'video' },
          { id: 'l4-3', title: '高頻行為題 20 題逐一演練', duration: '80 分鐘', type: 'project' },
          { id: 'l4-4', title: '文化契合度（Culture Fit）展示策略', duration: '35 分鐘', type: 'video' },
        ],
      },
      {
        id: 'ch5',
        title: '第五章：Offer 談判與入職',
        description: '拿到 Offer 只是開始，談判才是核心',
        lessons: [
          { id: 'l5-1', title: '薪酬結構解析：Base / Bonus / RSU', duration: '40 分鐘', type: 'video' },
          { id: 'l5-2', title: '談判腳本與話術範本', duration: '45 分鐘', type: 'video' },
          { id: 'l5-3', title: '多 Offer 比較與最優選擇策略', duration: '30 分鐘', type: 'video' },
          { id: 'l5-4', title: '90 天入職成功計畫', duration: '35 分鐘', type: 'reading' },
        ],
      },
    ],
    courseReviews: [
      {
        id: 'r1',
        name: '張偉康',
        avatar: '/assets/readdy/review-faang-1-80x80-squarish-a399c426.jpg',
        rating: 5,
        date: '2026 年 2 月',
        content: '系統設計那章真的封神，NexusGo 導師給的 FRDAS 框架讓我在 Google Onsite 每道系統設計都有條不紊地展開，面試官直接給我正向回饋。最終 L5 SWE Offer 到手！',
        company: '頂尖科技公司',
        result: '頂尖科技公司 L5 SWE Offer',
      },
      {
        id: 'r2',
        name: '王佳欣',
        avatar: '/assets/readdy/review-faang-2-80x80-squarish-6caddb19.jpg',
        rating: 5,
        date: '2026 年 1 月',
        content: '談判那章直接幫我多談了 $25k。導師說的「沉默是金」策略真的好用，HR 主動往上加了兩輪。這門課的 ROI 是所有課程裡最高的。',
        company: '頭部網路公司',
        result: '薪資多談 $25,000',
      },
    ],
  },
  {
    id: 'resume-linkedin-pro',
    title: 'AI 時代履歷 & LinkedIn 精修',
    subtitle: '讓 HR 主動找上門的個人品牌打造',
    description: '由前 McKinsey 顧問、現職涯發展教練 Sarah Kim 主講。在 AI 履歷篩選器盛行的時代，如何讓你的履歷通過 ATS 並打動 Hiring Manager？本課程提供完整的履歷優化體系、LinkedIn SEO 策略和個人品牌建設路線圖，已協助超過 5000 名學員讓 HR 主動聯繫。',
    instructor: 'Sarah Kim',
    instructorCompany: 'McKinsey',
    instructorDetail: {
      name: 'Sarah Kim',
      title: 'Career Strategy Coach & Ex-McKinsey',
      company: 'McKinsey',
      companyLogo: '/assets/readdy/fallback-mckinsey.svg',
      avatar: '/assets/readdy/instructor-priya-1-200x200-squarish-b937aaa4.jpg',
      bio: 'Sarah Kim 曾在 McKinsey 擔任高級顧問，專注於科技和金融行業的人才策略專案。離開顧問公司後，她創立了自己的職涯發展品牌，在過去 5 年中協助超過 5000 名亞裔專業人士成功進入外商公司。她獨創的「IMPACT 履歷框架」被多家頂尖 HR 顧問公司採用，是 LinkedIn 平台上擁有 15 萬+ 粉絲的知名職涯發展 KOL。',
      experience: '10 年職涯發展顧問經驗',
      stats: [
        { label: '輔導學員', value: '5,000+' },
        { label: '課程評分', value: '4.92' },
        { label: 'LinkedIn 粉絲', value: '15 萬+' },
        { label: '工作經驗', value: '10 年+' },
      ],
      expertise: ['履歷優化', 'LinkedIn SEO', '個人品牌', 'Cold Email', '面試準備', '職涯規劃'],
      mentorId: 'sarah-kim',
    },
    level: '入門',
    duration: '10 小時',
    students: 5124,
    rating: 4.92,
    reviews: 893,
    price: 299,
    originalPrice: 499,
    tag: '新上線',
    tagColor: 'bg-sky-500',
    gradient: 'from-orange-500 to-rose-600',
    icon: 'ri-file-user-line',
    category: '策略顧問',
    topics: ['履歷優化', 'LinkedIn SEO', '個人品牌', 'Cold Email'],
    lastUpdated: '2026 年 3 月',
    language: '中文',
    certificate: true,
    learningObjectives: [
      '掌握 ATS 友善的履歷撰寫框架，通過率提升 3 倍',
      '學會 LinkedIn SEO 優化，讓獵頭主動找到你',
      '打造獨特的個人品牌敘事，在同質化競爭中脫穎而出',
      '掌握有效的 Cold Email 和 LinkedIn 冷連結策略',
      '了解不同行業和公司的履歷期望差異',
      '建立持續更新和維護個人品牌的長效系統',
    ],
    requirements: [
      '有基本的工作經歷（實習生也適用）',
      '希望換工作或提升職業競爭力',
      '有 LinkedIn 帳號或願意建立',
      '無特殊技術要求',
    ],
    chapters: [
      {
        id: 'ch1',
        title: '第一章：AI 時代的求職新規則',
        description: '了解 AI 篩選機制，從源頭打贏求職賽局',
        lessons: [
          { id: 'l1-1', title: '課程介紹 & 學員成功案例', duration: '12 分鐘', type: 'video', preview: true },
          { id: 'l1-2', title: 'ATS 系統深度解析：HR 不會告訴你的秘密', duration: '35 分鐘', type: 'video', preview: true },
          { id: 'l1-3', title: '2026 求職市場趨勢與機會分析', duration: '28 分鐘', type: 'video' },
          { id: 'l1-4', title: '你的履歷自我診斷工具包', duration: '20 分鐘', type: 'project' },
        ],
      },
      {
        id: 'ch2',
        title: '第二章：IMPACT 履歷框架',
        description: '用數字和故事讓每一行經歷都價值千金',
        lessons: [
          { id: 'l2-1', title: 'IMPACT 框架：6 步寫出強力經歷描述', duration: '45 分鐘', type: 'video' },
          { id: 'l2-2', title: '量化你的成就：數字挖掘工作坊', duration: '40 分鐘', type: 'project' },
          { id: 'l2-3', title: '技術 vs 非技術職缺的差異化策略', duration: '30 分鐘', type: 'video' },
          { id: 'l2-4', title: '履歷格式與設計：什麼時候要「好看」', duration: '25 分鐘', type: 'video' },
          { id: 'l2-5', title: '真實履歷批改示範（5 個案例）', duration: '55 分鐘', type: 'video' },
        ],
      },
      {
        id: 'ch3',
        title: '第三章：LinkedIn 主頁全面優化',
        description: '把 LinkedIn 變成你的 24 小時主動求職引擎',
        lessons: [
          { id: 'l3-1', title: 'LinkedIn 演算法機制：如何被獵頭看見', duration: '38 分鐘', type: 'video' },
          { id: 'l3-2', title: 'Headline 和 About 的高級寫法', duration: '35 分鐘', type: 'video' },
          { id: 'l3-3', title: '推薦信策略：如何獲得有份量的背書', duration: '30 分鐘', type: 'video' },
          { id: 'l3-4', title: 'LinkedIn 個人主頁實戰優化', duration: '45 分鐘', type: 'project' },
        ],
      },
    ],
    courseReviews: [
      {
        id: 'r1',
        name: '吳思妤',
        avatar: '/assets/readdy/review-eng-1-80x80-squarish-9f20354d.jpg',
        rating: 5,
        date: '2026 年 2 月',
        content: '照著 Sarah 的方法改履歷和 LinkedIn 後，兩週內收到 8 封獵頭訊息，其中 3 個是 FAANG 公司！IMPACT 框架太實用了，每一條經歷都能寫得有說服力。',
        company: 'Stripe',
        result: '兩週內收到 8 封獵頭邀請',
      },
      {
        id: 'r2',
        name: 'Kevin Liao',
        avatar: '/assets/readdy/review-faang-1-80x80-squarish-a399c426.jpg',
        rating: 5,
        date: '2026 年 1 月',
        content: '身為應屆生覺得自己沒什麼經歷可寫，但 Sarah 的量化框架讓我發現了很多一直忽視的亮點。最終履歷通過率從 5% 提升到 35%。',
        company: 'Grab',
        result: '履歷通過率提升 600%',
      },
    ],
  },
  {
    id: 'ml-system-design',
    title: 'ML 系統設計實戰',
    subtitle: 'Staff Engineer 級別的大規模 ML 架構',
    description: '由 Microsoft Azure AI 資深架構師 Priya Sharma 主講，這是專為 Senior/Staff ML Engineer 設計的進階課程。深入剖析真實生產環境中的大規模機器學習系統設計，包含 Netflix 推薦系統、Uber 即時定價、TikTok 內容分發等 10+ 經典案例的完整架構解析。',
    instructor: 'Priya Sharma',
    instructorCompany: 'Microsoft',
    instructorDetail: {
      name: 'Priya Sharma',
      title: 'Principal ML Architect',
      company: 'Microsoft',
      companyLogo: '/assets/readdy/logo-microsoft-1-80x80-squarish-aac67d77.jpg',
      avatar: '/assets/readdy/instructor-priya-1-200x200-squarish-b937aaa4.jpg',
      bio: 'Priya Sharma 是 Microsoft Azure AI 的首席 ML 架構師，負責 Azure 機器學習平台的核心基礎架構設計。擁有 IIT 孟買資訊科學學士和 CMU 機器學習碩士學位，在微軟工作 9 年，主導了涵蓋數百萬使用者的生產級 ML 系統。同時擔任 NeurIPS、ICML 等頂會的審稿人，是分散式 ML 訓練領域的權威專家。',
      experience: '9 年大規模 ML 系統經驗',
      stats: [
        { label: '輔導學員', value: '280+' },
        { label: '課程評分', value: '4.95' },
        { label: '開源專案', value: '5 個' },
        { label: '工作經驗', value: '9 年+' },
      ],
      expertise: ['分散式訓練', 'MLOps', '特徵工程', '模型部署', 'ML 架構', '系統可擴展性'],
      mentorId: 'priya-sharma',
    },
    level: '高階',
    duration: '28 小時',
    students: 1873,
    rating: 4.95,
    reviews: 312,
    price: 599,
    originalPrice: 999,
    tag: '限時優惠',
    tagColor: 'bg-violet-600',
    gradient: 'from-sky-600 to-blue-800',
    icon: 'ri-server-line',
    category: 'ML 工程',
    topics: ['分散式訓練', 'MLOps', '特徵工程', '模型部署'],
    lastUpdated: '2026 年 2 月',
    language: '中文',
    certificate: true,
    learningObjectives: [
      '掌握大規模 ML 系統的完整設計方法論',
      '深入理解特徵儲存、模型服務、線上學習等核心模組',
      '學會評估和選擇適合不同業務場景的 ML 架構',
      '能夠獨立設計 Staff Engineer 水準的 ML 系統方案',
      '掌握 MLOps 最佳實踐：CI/CD、監控、飄移偵測',
      '了解如何優化 ML 系統的延遲、吞吐量和成本',
    ],
    requirements: [
      '扎實的 Python 程式設計能力',
      '有 ML 基礎（熟悉常見演算法）',
      '了解基本的分散式系統概念',
      '有 2 年以上 ML 工程經驗',
    ],
    chapters: [
      {
        id: 'ch1',
        title: '第一章：ML 系統設計方法論',
        description: '從業務目標到系統架構的完整思維鏈',
        lessons: [
          { id: 'l1-1', title: 'ML 系統 vs 傳統軟體系統的本質差異', duration: '35 分鐘', type: 'video', preview: true },
          { id: 'l1-2', title: '4 步 ML 系統設計框架', duration: '45 分鐘', type: 'video', preview: true },
          { id: 'l1-3', title: '案例：從頭設計 Google 廣告點擊率預測系統', duration: '70 分鐘', type: 'video' },
          { id: 'l1-4', title: '離線 vs 線上學習：如何選擇', duration: '40 分鐘', type: 'video' },
        ],
      },
      {
        id: 'ch2',
        title: '第二章：特徵工程與特徵儲存',
        description: '資料品質決定模型上限',
        lessons: [
          { id: 'l2-1', title: '特徵工程最佳實踐', duration: '55 分鐘', type: 'video' },
          { id: 'l2-2', title: 'Feature Store 架構設計', duration: '60 分鐘', type: 'video' },
          { id: 'l2-3', title: '即時特徵計算與批次特徵的融合', duration: '50 分鐘', type: 'video' },
          { id: 'l2-4', title: '特徵儲存實戰：Feast / Tecton 比較', duration: '45 分鐘', type: 'project' },
        ],
      },
      {
        id: 'ch3',
        title: '第三章：模型訓練與分散式計算',
        description: '從單機到多機多卡的訓練優化',
        lessons: [
          { id: 'l3-1', title: '分散式訓練策略：DP / MP / PP', duration: '65 分鐘', type: 'video' },
          { id: 'l3-2', title: 'GPU 叢集排程與資源優化', duration: '55 分鐘', type: 'video' },
          { id: 'l3-3', title: '混合精度訓練與梯度檢查點', duration: '50 分鐘', type: 'video' },
          { id: 'l3-4', title: '訓練效率優化實戰', duration: '80 分鐘', type: 'project' },
          { id: 'l3-5', title: '第三章知識測驗', duration: '20 分鐘', type: 'quiz' },
        ],
      },
      {
        id: 'ch4',
        title: '第四章：模型服務與部署',
        description: '讓模型在生產環境中穩定高效運行',
        lessons: [
          { id: 'l4-1', title: '模型壓縮：量化／剪枝／蒸餾', duration: '60 分鐘', type: 'video' },
          { id: 'l4-2', title: '推論服務優化：TensorRT / ONNX', duration: '55 分鐘', type: 'video' },
          { id: 'l4-3', title: 'A/B 測試與漸進式發布策略', duration: '45 分鐘', type: 'video' },
          { id: 'l4-4', title: '模型監控與資料飄移偵測', duration: '50 分鐘', type: 'video' },
        ],
      },
    ],
    courseReviews: [
      {
        id: 'r1',
        name: '孫浩然',
        avatar: '/assets/readdy/review-ml-1-80x80-squarish-28c7e971.jpg',
        rating: 5,
        date: '2026 年 1 月',
        content: '做了 3 年 ML 工程師但一直在做小規模系統，Priya 的課讓我第一次系統地理解了生產級 ML 架構。課程裡的 Feature Store 那章直接改變了我們團隊的工作方式。',
        company: 'ByteDance',
        result: '晉升 Staff Engineer',
      },
      {
        id: 'r2',
        name: 'Emily Wu',
        avatar: '/assets/readdy/review-ml-2-80x80-squarish-823fe8f6.jpg',
        rating: 5,
        date: '2025 年 12 月',
        content: '頂會級別的內容，以課程的價格來看簡直不可思議。系統設計面試時直接用 Priya 給的框架，面試官說這是他們收到過最完整的設計方案之一。',
        company: 'Apple',
        result: '拿到 Apple ML Engineer Offer',
      },
    ],
  },
  {
    id: 'english-interview-pro',
    title: '外商英文面試突破',
    subtitle: '用母語者思維征服每一輪英文面試',
    description: '由 Meta 資深軟體工程師 Marcus Johnson 主講，專為英文非母語的技術人才設計。本課程不只是語言課，而是用母語者的思維框架重塑你的英文面試表達，從技術討論到文化適配，全面提升你在英文環境下的專業形象和溝通說服力。',
    instructor: 'Marcus Johnson',
    instructorCompany: 'Meta',
    instructorDetail: {
      name: 'Marcus Johnson',
      title: 'Senior Software Engineer',
      company: 'Meta',
      companyLogo: '/assets/readdy/logo-meta-1-80x80-squarish-86cd48c6.jpg',
      avatar: '/assets/readdy/instructor-marcus-1-200x200-squarish-5b563b20.jpg',
      bio: 'Marcus Johnson 是 Meta 的資深軟體工程師，同時也是一位多元文化職場顧問。出生於美國，在亞洲生活工作超過 6 年，深度理解東西方職場文化差異。曾擔任 Meta 的多元包容性（D&I）專案負責人，輔導數百名非英語母語工程師提升跨文化溝通能力。他的課程以實用、接地氣著稱，專注於「說得對」而不是「說得完美」。',
      experience: '6 年亞洲職場經驗',
      stats: [
        { label: '輔導學員', value: '420+' },
        { label: '課程評分', value: '4.89' },
        { label: 'D&I 專案', value: '3 個' },
        { label: '教學經驗', value: '6 年+' },
      ],
      expertise: ['英文表達', 'STAR 法則', '文化適配', '發音矯正', '技術面試英文', '跨文化溝通'],
      mentorId: 'marcus-johnson',
    },
    level: '進階',
    duration: '16 小時',
    students: 4217,
    rating: 4.89,
    reviews: 756,
    price: 399,
    originalPrice: 699,
    tag: '口碑推薦',
    tagColor: 'bg-green-600',
    gradient: 'from-pink-500 to-violet-600',
    icon: 'ri-speak-line',
    category: 'AI 研究',
    topics: ['英文自我介紹', 'STAR 法則', '文化適配', '發音矯正'],
    lastUpdated: '2026 年 1 月',
    language: '中文（英文實戰演練）',
    certificate: true,
    learningObjectives: [
      '掌握英文面試常見題型的專業表達框架',
      '學會用母語者的方式敘述技術經驗和成就',
      '提升在壓力環境下的英文即興表達能力',
      '了解美國職場文化隱性規則和溝通禁忌',
      '建立自信的英文專業形象和個人敘事',
      '能夠在英文技術討論中清晰表達複雜概念',
    ],
    requirements: [
      '英文閱讀能力流利（多益 700+ 以上）',
      '希望進入英文工作環境',
      '有基本的專業工作經驗',
      '願意開口練習，不怕犯錯',
    ],
    chapters: [
      {
        id: 'ch1',
        title: '第一章：重建英文面試心態',
        description: '從害怕說錯到敢於表達的心理重塑',
        lessons: [
          { id: 'l1-1', title: '為什麼你的英文夠好但面試表現不好', duration: '25 分鐘', type: 'video', preview: true },
          { id: 'l1-2', title: '母語者 vs 非母語者的表達差異分析', duration: '35 分鐘', type: 'video', preview: true },
          { id: 'l1-3', title: '建立高情商的「非完美英文」策略', duration: '30 分鐘', type: 'video' },
          { id: 'l1-4', title: '自我錄音診斷練習', duration: '20 分鐘', type: 'project' },
        ],
      },
      {
        id: 'ch2',
        title: '第二章：英文自我介紹的藝術',
        description: '兩分鐘的開場白決定面試官的第一印象',
        lessons: [
          { id: 'l2-1', title: '高效自我介紹的 3 段式結構', duration: '38 分鐘', type: 'video' },
          { id: 'l2-2', title: '技術背景如何用非技術語言表達', duration: '35 分鐘', type: 'video' },
          { id: 'l2-3', title: '20 種開場白範本（附錄音示例）', duration: '45 分鐘', type: 'reading' },
          { id: 'l2-4', title: '自我介紹實戰錄製 & 回饋', duration: '30 分鐘', type: 'project' },
        ],
      },
      {
        id: 'ch3',
        title: '第三章：英文行為面試 STAR 進階',
        description: '用英文講出有說服力的工作故事',
        lessons: [
          { id: 'l3-1', title: '英文 STAR 故事的節奏與語氣控制', duration: '45 分鐘', type: 'video' },
          { id: 'l3-2', title: '20 個高頻行為題英文回答示範', duration: '90 分鐘', type: 'video' },
          { id: 'l3-3', title: '文化適配：什麼話說了會扣分', duration: '40 分鐘', type: 'video' },
          { id: 'l3-4', title: '模擬面試實戰練習', duration: '60 分鐘', type: 'project' },
        ],
      },
    ],
    courseReviews: [
      {
        id: 'r1',
        name: '蔡宜蓁',
        avatar: '/assets/readdy/review-eng-1-80x80-squarish-9f20354d.jpg',
        rating: 5,
        date: '2025 年 12 月',
        content: 'Marcus 那句「說得對比說得完美更重要」徹底改變了我的心態。以前一緊張就忘詞，現在面試的時候反而更專注在溝通本身。最終拿到了 Amazon 的 Offer。',
        company: 'Amazon',
        result: '拿到 Amazon SDE Offer',
      },
    ],
  },
  {
    id: 'data-science-career',
    title: '資料科學職涯加速器',
    subtitle: '從分析師到高級科學家的完整成長地圖',
    description: '由 Amazon 資料科學團隊 Lead Wei Zhang 主講，專為想要在資料科學領域快速晉升的專業人士設計。本課程從技術深度（A/B 測試、因果推斷、SQL 進階）到職場軟技能（資料說故事、向上匯報、影響力建立），提供一套完整的資料科學職涯加速框架。',
    instructor: 'Wei Zhang',
    instructorCompany: 'Amazon',
    instructorDetail: {
      name: 'Wei Zhang',
      title: 'Data Science Lead',
      company: 'Amazon',
      companyLogo: '/assets/readdy/logo-amazon-1-80x80-squarish-d4bc7a9a.jpg',
      avatar: '/assets/readdy/instructor-wei-1-200x200-squarish-12777c73.jpg',
      bio: 'Wei Zhang 是 Amazon 資料科學團隊的 Lead，負責 Amazon 核心推薦系統和動態定價模型的資料科學研究。在加入 Amazon 之前，曾在 Airbnb 和 LinkedIn 工作，全程經歷了從初級分析師到 Lead 的完整晉升路徑。他是 Amazon 內部最受歡迎的資料科學 Mentor，也在華人資料科學社群擁有廣泛影響力，經常在各大資料科學峰會發表演講。',
      experience: '10 年資料科學經驗',
      stats: [
        { label: '輔導學員', value: '350+' },
        { label: '課程評分', value: '4.91' },
        { label: '演講經歷', value: '15 場' },
        { label: '工作經驗', value: '10 年+' },
      ],
      expertise: ['A/B 測試', '因果推斷', '資料說故事', 'SQL 進階', '機器學習', '職涯規劃'],
      mentorId: 'wei-zhang',
    },
    level: '進階',
    duration: '20 小時',
    students: 2641,
    rating: 4.91,
    reviews: 428,
    price: 449,
    originalPrice: 799,
    tag: '推薦',
    tagColor: 'bg-amber-600',
    gradient: 'from-teal-500 to-cyan-700',
    icon: 'ri-bar-chart-box-line',
    category: '資料科學',
    topics: ['A/B 測試', '因果推斷', '資料說故事', 'SQL 進階'],
    lastUpdated: '2026 年 2 月',
    language: '中文',
    certificate: true,
    learningObjectives: [
      '深度掌握實驗設計與 A/B 測試的完整方法論',
      '理解因果推斷的核心原理和實際應用',
      '學會用「資料說故事」的方式影響業務決策',
      '掌握 SQL 進階技巧：窗口函數、CTEs、效能優化',
      '建立向上匯報和跨團隊協作的資料科學影響力',
      '了解從分析師到 Lead 的完整晉升路徑和策略',
    ],
    requirements: [
      '有 1-2 年資料分析或資料科學工作經驗',
      '熟悉基本的 SQL 和 Python',
      '了解基本統計知識',
      '希望在資料領域快速晉升',
    ],
    chapters: [
      {
        id: 'ch1',
        title: '第一章：資料科學職涯路徑規劃',
        description: '清楚認識不同層級的能力要求和晉升邏輯',
        lessons: [
          { id: 'l1-1', title: 'Amazon 資料科學職級體系解析', duration: '30 分鐘', type: 'video', preview: true },
          { id: 'l1-2', title: '分析師 vs 科學家 vs Lead 的核心差異', duration: '35 分鐘', type: 'video', preview: true },
          { id: 'l1-3', title: '個人職涯落差分析工作坊', duration: '25 分鐘', type: 'project' },
          { id: 'l1-4', title: '12 個月加速晉升計畫制定', duration: '30 分鐘', type: 'video' },
        ],
      },
      {
        id: 'ch2',
        title: '第二章：A/B 測試與實驗設計',
        description: '用嚴謹的實驗方法推動產品和業務決策',
        lessons: [
          { id: 'l2-1', title: 'A/B 測試完整方法論', duration: '55 分鐘', type: 'video' },
          { id: 'l2-2', title: '樣本數計算與統計檢定力分析', duration: '50 分鐘', type: 'video' },
          { id: 'l2-3', title: '常見實驗陷阱與偏差識別', duration: '45 分鐘', type: 'video' },
          { id: 'l2-4', title: 'Amazon 真實 A/B 測試案例分析', duration: '65 分鐘', type: 'video' },
          { id: 'l2-5', title: '實驗設計專案實戰', duration: '60 分鐘', type: 'project' },
        ],
      },
      {
        id: 'ch3',
        title: '第三章：因果推斷進階',
        description: '超越相關性，找到真正推動業務的因果關係',
        lessons: [
          { id: 'l3-1', title: '因果推斷基礎：潛在結果框架', duration: '50 分鐘', type: 'video' },
          { id: 'l3-2', title: '斷點迴歸設計（RDD）實戰', duration: '55 分鐘', type: 'video' },
          { id: 'l3-3', title: '雙重差分（DID）與合成控制', duration: '60 分鐘', type: 'video' },
          { id: 'l3-4', title: '因果推斷在 Amazon 定價中的應用', duration: '70 分鐘', type: 'video' },
        ],
      },
    ],
    courseReviews: [
      {
        id: 'r1',
        name: '李雨桐',
        avatar: '/assets/readdy/review-ds-1-80x80-squarish-6f8c07e3.jpg',
        rating: 5,
        date: '2026 年 2 月',
        content: 'Wei 對 A/B 測試的講解真的是我見過最清晰的。我用課裡學到的樣本數計算方法設計了一個關鍵實驗，結果直接成為了季度最有影響力的專案，晉升提案獲批了！',
        company: 'Shopee',
        result: '成功晉升 Senior Data Scientist',
      },
      {
        id: 'r2',
        name: '劉宗翰',
        avatar: '/assets/readdy/review-ds-2-80x80-squarish-d5e46738.jpg',
        rating: 5,
        date: '2025 年 11 月',
        content: '因果推斷那章是全網最好的中文教程沒有之一。搭配 Amazon 真實案例，理論和實踐無縫結合。已經在工作中用斷點迴歸設計了兩個專案，老闆非常滿意。',
        company: 'LINE',
        result: '團隊影響力大幅提升',
      },
    ],
  },
];

export const featuredCourses = allCourses.slice(0, 4);
